<template>
  <div class="c-pay-off-chart">
    <v-stock-chart class="chart" :options="chartOptions" :api="chart" theme="dark" :height="140" ref="payoffChart" />
  </div>
</template>

<script>
import { isOption, isStock, isBuy } from '@/utils/optionChainServices/legService';
import { StrategyService } from '@/utils/optionChainServices/strategyService';
import { getTimeToMaturityInYears } from '@/utils/optionChainServices/helpers/date';
import { Greeks } from '@/utils/optionChainServices/calculationService';

import VStockChart from '@base/VStockChart';

export default {
  name: 'c-pay-off-chart',
  components: {
    VStockChart
  },
  props: {
    legs: {
      type: Object,
      required: true
    },
    underlyingData: {
      type: Object,
      required: true
    },
    interestRateData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chart: {},
      chartOptions: {
        lang: {
          accessibility: {
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        accessibility: {
          enabled: true,
          keyboardNavigation: {
            enabled: true,
            seriesNavigation: {
              mode: 'normal'
            }
          },
          series: { pointDescriptionEnabledThreshold: false },
          point: {
            descriptionFormatter: undefined
          },
          screenReaderSection: {}
        },
        chart: {
          zoomType: 'xy',
          type: 'area',
          marginTop: 5,
          marginRight: 50,
          marginBottom: 25,
          marginLeft: 1,
          padding: 0
        },
        navigator: {
          enabled: false
        },
        scrollbar: {
          enabled: false
        },
        rangeSelector: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        yAxis: {
          labels: {
            formatter: params => this.$f(params.value, 'float.price'),
            x: 50
          },
          plotLines: [
            {
              value: 0,
              zIndex: 3
            }
          ],
          startOnTick: false,
          endOnTick: false,
          minPadding: 0,
          accessibility: {
            rangeDescription: ''
          }
        },
        xAxis: {
          labels: {
            formatter: params => this.$f(params.value, 'float.price')
          },
          startOnTick: false,
          endOnTick: false,
          minPadding: 0,
          accessibility: {
            description: this.$t(`white-label.widgets.optionChain.labels.underlying`),
            rangeDescription: ''
          }
        },
        plotOptions: {
          series: {
            softThreshold: true,
            turboThreshold: Infinity,
            negativeColor: true
          }
        },
        tooltip: {
          useHTML: true,
          headerFormat: '<div>',
          pointFormat: `<p> ${this.$t(
            `white-label.widgets.optionChain.labels.underlying`
          )} : {point.x:.2f}</p><p>${this.$t(`white-label.widgets.optionChain.labels.expiry`)} : {point.y:.2f}</p>`,
          footerFormat: '</div>',
          followPointer: true,
          split: false
        },
        series: [
          {
            id: 'payoffSeries',
            name: 'PayOff',
            type: 'area',
            yAxis: 0,
            dataGrouping: {
              enabled: false
            }
          }
        ]
      }
    };
  },
  watch: {
    legs: {
      handler: 'handleLegs',
      deep: true
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    handleLegs() {
      if (this.legs.first || this.legs.second) {
        const chartData = this.processGainLossDataPoints(
          this.underlyingData['q._TRDPRC_1'],
          this.interestRateData['q.PRIMACT_1'],
          this.underlyingData['q._YIELD'],
          this.legs
        );

        this.chart.get('payoffSeries').update({
          data: chartData
        });

        // set rangeDescription for yAxis in chartOptions
        this.setRangeDescription();
        this.chartOptions.accessibility.point.descriptionFormatter = this.descriptionFormatter;
      } else {
        this.resetPayOffChart();
      }
    },
    /**
     * @param {String} underlyingPrice stock price
     * @param {String} interestRate underlying interest rate
     * @param {String} dividendYield underlying yield
     * @param {Object} legs current selected legs
     *
     * @return {Array} dataPoints data points for Highcharts
     */
    processGainLossDataPoints(underlyingPrice, interestRate, dividendYield, legs) {
      const dataPoints = [];
      const underlyingPrices = []; // Array of underlying prices the chart will be drawn for

      const calendarSpreads = [
        'LONG_CALL_CALENDAR',
        'LONG_PUT_CALENDAR',
        'SHORT_CALL_CALENDAR',
        'SHORT_PUT_CALENDAR',
        'DIAGONAL_BULL_CALL_SPREAD',
        'DIAGONAL_BEAR_CALL_SPREAD',
        'DIAGONAL_BULL_PUT_SPREAD',
        'DIAGONAL_BEAR_PUT_SPREAD'
      ];
      const currentStrategy = StrategyService.detectOptionStrategy(legs.first, legs.second);

      let meanStrike = 0;
      let validLegs = 0;

      Object.values(legs).forEach(leg => {
        if (leg) {
          if (isOption(leg)) {
            meanStrike += parseFloat(leg.option['xopt._STRIKE']);
          } else {
            meanStrike += parseFloat(underlyingPrice);
          }
          validLegs++;
        }
      });

      if (validLegs > 0) {
        meanStrike /= validLegs;
      }

      // if strategy is Calendar Spread, we need to calculate the payoff at maturity of the front-month option.
      // For all other strategies, we need to calculate the payoff at maturity (which is an easier function)
      // If current strategy is a calendar spread -> Determine the time T that we use to price the option (in 99% this is at maturity, but for calendar spread ...?)
      let timeMin = 1000;
      if (calendarSpreads.includes(currentStrategy)) {
        //console.log('is calendar spread');
        Object.values(legs).forEach(leg => {
          if (leg) {
            if (isOption(leg)) {
              const t_i = getTimeToMaturityInYears(leg.option['xopt._MATURITY_ISO']);
              if (t_i < timeMin) {
                timeMin = t_i;
              }
            }
          }
        });
      }

      // Generate list of underlying prices (mean strike +/- 10) at a distance from each other depending on the meanStrike size,
      // e.g. $1 if underlying price is lower than $100, $5 if stock price is above $100
      const priceDistance = meanStrike < 100 ? 10 : 50;
      for (let i = meanStrike - priceDistance; i < meanStrike + priceDistance; i = i + priceDistance / 100) {
        underlyingPrices.push(Math.round(i * 100) / 100);
      }

      // Process data points for every price in underlyingPrices
      underlyingPrices.forEach((strike, i) => {
        let IV = 0;

        Object.values(legs).forEach(leg => {
          if (leg) {
            if (isStock(leg)) {
              IV += (isBuy(leg) ? 1 : -1) * (Math.max(underlyingPrices[i], 0) - underlyingPrice) * leg.quantity;
            } else if (isOption(leg)) {
              const NetDebit = (isBuy(leg) ? leg.option['q._ASK'] : -leg.option['q._BID']) * leg.quantity; // TODO: Change q._ASK with q._BID

              // If current strategy is a calendar spread
              if (calendarSpreads.includes(currentStrategy)) {
                //console.log('is calendar spread');
                // payoff at expiration of front-month option
                const t_i = getTimeToMaturityInYears(leg.option['xopt._MATURITY_ISO']);
                const r = interestRate / 100;
                const r_d = dividendYield / 100;
                const b = r - r_d;

                let price = null;
                if (t_i - timeMin < 1e-2) {
                  // simple heuristic to see if option is (numerically) at maturity
                  price = this.optionIntrinsicValue(
                    leg.option['xopt._TYPE_CP'],
                    underlyingPrices[i],
                    leg.option['xopt._STRIKE']
                  );
                } else {
                  price = Greeks.GBSOption(
                    leg.option['xopt._TYPE_CP'],
                    underlyingPrices[i],
                    leg.option['xopt._STRIKE'],
                    t_i - timeMin,
                    r,
                    b,
                    leg.option['q._VOLA_ASK'] / 100
                  );
                }

                IV += ((isBuy(leg) ? 1 : -1) * price * leg.quantity - NetDebit) * leg.option['xopt.LOT_SIZE_A'];
              } else {
                // payoff at maturity
                IV +=
                  ((isBuy(leg) ? 1 : -1) *
                    this.optionIntrinsicValue(
                      leg.option['xopt._TYPE_CP'],
                      underlyingPrices[i],
                      leg.option['xopt._STRIKE']
                    ) *
                    leg.quantity -
                    NetDebit) *
                  leg.option['xopt.LOT_SIZE_A'];
              }
            }
          }
        });

        dataPoints.push({
          x: underlyingPrices[i],
          y: IV
        });
      });

      return dataPoints;
    },
    optionIntrinsicValue: function(type, S, X) {
      if (type === 'C') {
        return Math.max(S - X, 0);
      } else if (type === 'P') {
        return Math.max(X - S, 0);
      } else {
        return NaN;
      }
    },
    setRangeDescription() {
      const minX = this.$f(this.$refs.payoffChart.chart.xAxis[0].min, 'float.price');
      const maxX = this.$f(this.$refs.payoffChart.chart.xAxis[0].max, 'float.price');
      this.chartOptions.xAxis.accessibility.rangeDescription = `Range: ${minX} to ${maxX}`;

      const min = this.$f(this.$refs.payoffChart.chart.yAxis[0].min, 'float.price');
      const max = this.$f(this.$refs.payoffChart.chart.yAxis[0].max, 'float.price');
      this.chartOptions.yAxis.accessibility.rangeDescription = `Range: ${min} to ${max}`;
    },
    resetPayOffChart() {
      this.chart.get('payoffSeries').update({
        data: []
      });
    },
    descriptionFormatter(point) {
      const y =
        point.index +
        this.$t(`white-label.widgets.optionChain.labels.underlying`) +
        point.x +
        this.$t(`white-label.widgets.optionChain.labels.expiry`) +
        this.$f(point.y, 'float.price');
      const output = [];
      output.push(y);

      return output.join(' ');
    },
    getChartTitle() {
      return `${this.$t(`white-label.widgets.optionChain.labels.customStrategy`)} ${this.$t(
        'white-label.globalLabels.chartGraphImage'
      )}`;
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-pay-off-chart';
</style>
